import type { Question, InspectionResult, QuestionStatus, ObjectInstanceDetails, QuestionGroup } from "../types";

/**
 * Gets the status of a question based on inspection results
 * 
 * @param questionId - The question ID to check status for
 * @param results - Array of inspection results
 * @returns Status object with answered state, compliance, display text, and color
 */
export const getQuestionStatus = (
  questionId: string,
  results: InspectionResult[]
): QuestionStatus => {
  const result = results.find((r) => r.questionId === questionId);

  if (!result) {
    return {
      answered: false,
      compliant: null,
      displayText: "Not Answered",
      colorType: "neutral",
      isPending: false,
    };
  }

  if (result.aiResult === "PENDING") {
    return {
      answered: true,
      compliant: null,
      displayText: "Analyzing...",
      colorType: "warning",
      isPending: true,
    };
  }

  return {
    answered: true,
    compliant: result.isCompliant ?? false,
    displayText: result.isCompliant ? "Compliant" : "Non-Compliant",
    colorType: result.isCompliant ? "success" : "error",
    isPending: false,
  };
};

/**
 * Groups questions by object type
 * Returns both object groups and individual questions
 * 
 * Questions with applicableObjectTypes are grouped together (e.g., all drum questions).
 * Questions without object types are treated as individual questions.
 * 
 * @param questions - Array of questions to group
 * @returns Object containing grouped questions and individual questions
 */
export const groupQuestionsByObjectType = (questions: Question[]): QuestionGroup => {
  const groups: { [objectType: string]: Question[] } = {};
  const individuals: Question[] = [];

  questions.forEach((q) => {
    if (q.applicableObjectTypes && q.applicableObjectTypes.length > 0) {
      q.applicableObjectTypes.forEach((objType: string) => {
        if (!groups[objType]) {
          groups[objType] = [];
        }
        groups[objType].push(q);
      });
    } else {
      individuals.push(q);
    }
  });

  return { groups, individuals };
};

/**
 * Gets instance count and details for an object type from results
 * Groups results by evidence URI to determine unique instances
 * 
 * For example, if you inspected 3 drums, this would return instanceCount: 3
 * and details about each drum instance.
 * 
 * @param objectType - The object type to get instances for (e.g., "Drum")
 * @param questions - Questions applicable to this object type
 * @param results - All inspection results
 * @returns Details about object instances including count and evidence URIs
 */
export const getObjectInstanceDetails = (
  objectType: string,
  questions: Question[],
  results: InspectionResult[]
): ObjectInstanceDetails => {
  const questionIds = questions.map((q) => q.questionId);
  const relevantResults = results.filter((r) => questionIds.includes(r.questionId));

  const evidenceUriMap = new Map<string, InspectionResult[]>();

  relevantResults.forEach((result) => {
    if (!result.evidence || result.evidence.length === 0 || result.aiResult === "PENDING") {
      return;
    }

    const evidenceUri = result.evidence[0]?.uri || "unknown";
    if (!evidenceUriMap.has(evidenceUri)) {
      evidenceUriMap.set(evidenceUri, []);
    }
    evidenceUriMap.get(evidenceUri)!.push(result);
  });

  const instances = Array.from(evidenceUriMap.entries())
    .map(([uri, instanceResults], index) => ({
      instanceNumber: index + 1,
      evidenceUri: uri,
      results: instanceResults,
    }))
    .filter((instance) => instance.results.length === questionIds.length); // Only complete instances

  return {
    instanceCount: instances.length,
    instances: instances.map((i) => ({
      instanceNumber: i.instanceNumber,
      evidenceUri: i.evidenceUri,
    })),
  };
};